import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} from './productKnowledgeApiSlice';
import {
  Header,
  Button,
  Icon,
  Modal,
  Container,
  Table,
  Form,
  Segment,
  Loader,
  Input,
  Pagination,
  Label,
} from 'semantic-ui-react';
import { PAGE_SIZE } from '../../constants';
import CustomDropdown from '../../components/CustomDropdown';

const ProductModal = ({ open, onClose, product = null }) => {
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();

  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || '',
    support_url: product?.support_url || '',
    parsed_specs: product?.parsed_specs || '',
  });

  const handleSubmit = async () => {
    try {
      if (product) {
        await updateProduct({ id: product.id, ...formData }).unwrap();
      } else {
        await createProduct(formData).unwrap();
      }
      onClose();
    } catch (err) {
      console.error('Failed to save product:', err);
    }
  };

  const handleChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>{product ? 'Edit Product' : 'Add Product'}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <Form.Input
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          />
          <Form.Input
            label="Support URL"
            name="support_url"
            value={formData.support_url}
            onChange={handleChange}
          />
          <Form.TextArea
            label="Parsed Specs"
            name="parsed_specs"
            value={formData.parsed_specs}
            onChange={handleChange}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary onClick={handleSubmit}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const ProductManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);

  const page = queryParams.get('page') || 1;
  const page_size = queryParams.get('page_size') || PAGE_SIZE;
  const searchName = queryParams.get('name') || '';
  const searchCategory = queryParams.get('category') || '';
  const ordering = queryParams.get('ordering') || '-release_year';

  const [localSearchName, setLocalSearchName] = useState(searchName);
  const [localSearchCategory, setLocalSearchCategory] =
    useState(searchCategory);

  const { data: allProducts } = useGetProductsQuery({
    page: 1,
    page_size: 1000,
  });

  const categoryOptions = React.useMemo(() => {
    if (!allProducts?.results) return [];
    const unique = [...new Set(allProducts.results.map(p => p.category).filter(Boolean))];
    return unique.sort().map(cat => ({ key: cat, text: cat, value: cat }));
  }, [allProducts]);

  const { data: products, isLoading } = useGetProductsQuery({
    page,
    page_size,
    name: searchName,
    category: searchCategory,
    ordering,
  });

  const [deleteProduct] = useDeleteProductMutation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEdit = product => {
    navigate(`/admin/products/${product.id}`);
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteProduct(id);
    }
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const updateQueryParams = updates => {
    const newParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    // Reset to page 1 on filter change
    if (!updates.page) {
      newParams.set('page', 1);
    }
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleSort = clickedColumn => {
    const currentOrdering = ordering || 'name';
    const currentColumn = currentOrdering.startsWith('-')
      ? currentOrdering.substring(1)
      : currentOrdering;
    const isDescending = currentOrdering.startsWith('-');

    let newOrdering;
    if (currentColumn === clickedColumn) {
      newOrdering = isDescending ? clickedColumn : `-${clickedColumn}`;
    } else {
      newOrdering = clickedColumn;
    }

    updateQueryParams({ ordering: newOrdering });
  };

  const getSortIcon = column => {
    const currentOrdering = ordering || 'name';
    const currentColumn = currentOrdering.startsWith('-')
      ? currentOrdering.substring(1)
      : currentOrdering;

    if (currentColumn !== column) return 'sort';
    return currentOrdering.startsWith('-') ? 'sort down' : 'sort up';
  };

  const handleSearch = () => {
    updateQueryParams({
      name: localSearchName,
      category: localSearchCategory,
    });
  };

  const handlePaginationChange = (e, { activePage }) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('page', activePage);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  if (isLoading) return <Loader active />;

  return (
    <div
      style={{
        height: isMobile ? 'auto' : 'auto',
        display: isMobile ? 'block' : 'flex',
        flexDirection: 'column',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          marginBottom: isMobile ? '20px' : '0',
        }}
      >
        <Container fluid>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '0 20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  flexShrink: 0, // stays a circle when the text beside it wraps
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  name="box"
                  style={{ margin: 0, color: '#0091FF', fontSize: '18px' }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1d1d1f',
                  }}
                >
                  제품 정보 관리
                </h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                  Manage product knowledge base
                </p>
              </div>
            </div>

            {/* Add Button */}
            <Button
              primary
              onClick={handleCreate}
              icon
              labelPosition="left"
              style={{
                borderRadius: '30px',
                height: '40px',
                backgroundColor: '#0091FF',
                paddingLeft: '20px',
                paddingRight: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="add" />
              제품 추가
            </Button>
          </div>
        </Container>
      </div>

      <Container
        fluid
        style={{
          padding: isMobile ? '0 20px' : '20px',
          flex: isMobile ? 'none' : 1,
          display: isMobile ? 'block' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          {/* Search Input Name */}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              height: '48px',
              gap: '12px',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon
              name="search"
              style={{ color: '#86868b', margin: 0, fontSize: '16px' }}
            />
            <Input
              placeholder="제품명 검색..."
              value={localSearchName}
              onChange={e => setLocalSearchName(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              transparent
              fluid
              style={{
                fontSize: '14px',
                width: '100%',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
          </div>

          {/* Search Input Category */}
          <CustomDropdown
            placeholder="카테고리 선택"
            options={categoryOptions}
            value={localSearchCategory}
            onChange={value => {
              setLocalSearchCategory(value || '');
              updateQueryParams({ category: value || '' });
            }}
            icon="filter"
            style={{ minWidth: '200px' }}
          />

          {/* Search Button */}
          <div
            style={{
              marginLeft: isMobile ? '0' : 'auto',
              width: isMobile ? '100%' : 'auto',
              display: 'flex',
              gap: '8px',
            }}
          >
            <Button
              primary
              icon
              labelPosition="right"
              onClick={handleSearch}
              fluid={isMobile}
              style={{
                borderRadius: '30px',
                height: '48px',
                backgroundColor: '#0091FF',
                paddingLeft: '16px',
                paddingRight: '16px',
                // display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              검색
              <Icon
                name="arrow right"
                style={{ marginLeft: '8px', margin: 0 }}
              />
            </Button>
          </div>

          {(searchName || searchCategory) && (
            <Button
              icon
              onClick={() => {
                setLocalSearchName('');
                setLocalSearchCategory('');
                updateQueryParams({ name: '', category: '' });
              }}
              style={{
                height: '36px',
                width: '36px',
                minWidth: '36px',
                borderRadius: '50%',
                backgroundColor: '#FF3B30',
                color: 'white',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
              }}
            >
              <Icon name="close" style={{ margin: 0, fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            </Button>
          )}
        </div>

        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div>
            <Table
              compact="very"
              selectable
              style={{ margin: 0, border: 'none' }}
            >
              <Table.Header
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'white',
                }}
              >
                <Table.Row>
                  <Table.HeaderCell
                    onClick={() => handleSort('name')}
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                      paddingLeft: '20px',
                    }}
                  >
                    제품명{' '}
                    <Icon
                      name={getSortIcon('name')}
                      style={{ marginLeft: 4 }}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    onClick={() => handleSort('category')}
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                  >
                    카테고리{' '}
                    <Icon
                      name={getSortIcon('category')}
                      style={{ marginLeft: 4 }}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    onClick={() => handleSort('release_year')}
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                  >
                    출시 연도{' '}
                    <Icon
                      name={getSortIcon('release_year')}
                      style={{ marginLeft: 4 }}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    onClick={() => handleSort('session_count')}
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                  >
                    세션 수{' '}
                    <Icon
                      name={getSortIcon('session_count')}
                      style={{ marginLeft: 4 }}
                    />
                  </Table.HeaderCell>
                  {/* <Table.HeaderCell
                    style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}
                  >
                    Support URL
                  </Table.HeaderCell> */}
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      textAlign: 'center',
                    }}
                  >
                    액션
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {products?.results?.map(product => (
                  <Table.Row key={product.id}>
                    <Table.Cell style={{ paddingLeft: '20px' }}>
                      {product.name}
                    </Table.Cell>
                    <Table.Cell>{product.category}</Table.Cell>
                    <Table.Cell>{product.release_year}</Table.Cell>
                    <Table.Cell>
                      
                        <Label as='a' onClick={() =>
                          navigate(`/chatsessionslist?product=${product.id}`)
                        } basic size="small">
                        {product.session_count}
                        </Label>
                    </Table.Cell>
                    {/* <Table.Cell>
                      {product.support_url && (
                        <a
                          href={product.support_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007AFF',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Icon name="external alternate" size="small" />
                          Link
                        </a>
                      )}
                    </Table.Cell> */}
                    <Table.Cell textAlign="right">
                        <Button
                        size='mini'
                          icon="eye"
                          content="상세"
                          onClick={() => handleEdit(product)}
                          style={{ borderRadius: '30px' }}
                        />
                        
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>

          {products?.count > 0 && (
            <div
              style={{
                padding: '12px',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
              }}
            >
              <Pagination
                activePage={page}
                totalPages={Math.ceil(products.count / page_size)}
                onPageChange={handlePaginationChange}
                size="mini"
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                nextItem={{ content: <Icon name="angle right" />, icon: true }}
                ellipsisItem={{
                  content: <Icon name="ellipsis horizontal" />,
                  icon: true,
                }}
                style={{ boxShadow: 'none' }}
              />
            </div>
          )}
        </div>

        {isModalOpen && (
          <ProductModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            product={selectedProduct}
          />
        )}
      </Container>
    </div>
  );
};

export default ProductManagement;
